import React, { useState } from 'react';
import {
  X,
  MapPin,
  Phone,
  User,
  Package,
  Clock,
  DollarSign,
  FileText,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Store,
  Calendar,
  Truck
} from 'lucide-react';

function DeliverySearchModal({ delivery, onClose }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  function getStatusColor(status) {
    switch (status) {
      case 'pending':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'encamino':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'entregado':
        return 'bg-[#67A43C]/10 text-[#67A43C] border-[#67A43C]';
      case 'cancelado':
      case 'rechazado':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-300';
    }
  }

  function getStatusLabel(status) {
    const labels = {
      pending: 'Pendiente',
      encamino: 'En Camino',
      entregado: 'Entregado',
      cancelado: 'Cancelado',
      rechazado: 'Rechazado',
    };
    return labels[status] || status;
  }

  const images = delivery.image_urls || [];
  const hasImages = images.length > 0;

  function nextImage() {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }

  function prevImage() {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-gradient-to-r from-[#B1336E]/5 to-[#E6B43C]/5">
          <div>
            <h2 className="text-xl font-extrabold text-[#B1336E] font-montserrat">
              Detalle de Entrega
            </h2>
            <p className="text-sm text-slate-600 font-montserrat mt-1">
              ID: #{delivery.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Status Badge */}
          <div className="flex justify-center">
            <span
              className={`px-6 py-3 text-base font-bold rounded-xl border-2 font-montserrat ${getStatusColor(
                delivery.status
              )}`}
            >
              {getStatusLabel(delivery.status)}
            </span>
          </div>

          {/* Images Carousel */}
          {hasImages && (
            <div className="bg-slate-50 rounded-xl overflow-hidden">
              <div className="relative aspect-video bg-slate-200">
                <img
                  src={images[currentImageIndex]}
                  alt={`Delivery ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
                
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 text-slate-700" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
                    >
                      <ChevronRight className="w-5 h-5 text-slate-700" />
                    </button>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {images.map((_, index) => (
                        <div
                          key={index}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentImageIndex
                              ? 'bg-white'
                              : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Delivery Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Recipient Name */}
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <User className="w-4 h-4" />
                <span className="text-xs font-medium font-montserrat uppercase">
                  Destinatario
                </span>
              </div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">
                {delivery.recipient_name || 'N/A'}
              </p>
            </div>

            {/* Store/User Name */}
            {delivery.user_name && (
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <Store className="w-4 h-4" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Tienda
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {delivery.user_name}
                </p>
              </div>
            )}

            {/* Phone */}
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <Phone className="w-4 h-4" />
                <span className="text-xs font-medium font-montserrat uppercase">
                  Teléfono
                </span>
              </div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">
                {delivery.recipient_phone || 'N/A'}
              </p>
            </div>

            {/* Delivery Date */}
            {delivery.delivery_date && (
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Fecha de Entrega
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {new Date(delivery.delivery_date).toLocaleDateString('es-PE', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
            )}

            {/* Address */}
            <div className="bg-slate-50 rounded-xl p-4 sm:col-span-2">
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <MapPin className="w-4 h-4" />
                <span className="text-xs font-medium font-montserrat uppercase">
                  Dirección
                </span>
              </div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">
                {delivery.address || 'N/A'}
              </p>
              {delivery.district && (
                <p className="text-xs text-slate-600 font-montserrat mt-1">
                  {delivery.district}
                </p>
              )}
            </div>

            {/* Product */}
            {delivery.product && (
              <div className="bg-slate-50 rounded-xl p-4 sm:col-span-2">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <Package className="w-4 h-4" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Producto
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {delivery.product}
                </p>
              </div>
            )}

            {/* Financial Information */}
            <div className="bg-gradient-to-r from-[#67A43C]/5 to-[#E6B43C]/5 rounded-xl p-4 border-l-4 border-[#67A43C]">
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs font-medium font-montserrat uppercase">
                  Monto a Cobrar
                </span>
              </div>
              <p className="text-lg font-bold text-[#67A43C] font-montserrat">
                S/ {(delivery.charge || 0).toFixed(2)}
              </p>
            </div>

            {/* Charged Status */}
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs font-medium font-montserrat uppercase">
                  Monto Cobrado
                </span>
              </div>
              <p className="text-lg font-bold text-slate-900 font-montserrat">
                {delivery.charged !== null && delivery.charged !== undefined
                  ? `S/ ${(delivery.charged || 0).toFixed(2)}`
                  : 'Pendiente'}
              </p>
            </div>

            {/* Arrival Time */}
            {delivery.arrival_time && (
              <div className="bg-slate-50 rounded-xl p-4 sm:col-span-2">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Hora de Llegada
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {new Date(delivery.arrival_time).toLocaleString('es-PE', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            )}

            {/* Driver Information */}
            {delivery.driver && (
              <div className="bg-slate-50 rounded-xl p-4 sm:col-span-2">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <Truck className="w-4 h-4" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Conductor
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {delivery.driver.username || 'N/A'}
                </p>
                {delivery.driver.phone_number && (
                  <p className="text-xs text-slate-600 font-montserrat mt-1">
                    {delivery.driver.phone_number}
                  </p>
                )}
              </div>
            )}

            {/* Driver Notes */}
            {delivery.notes_driver && (
              <div className="bg-amber-50 rounded-xl p-4 sm:col-span-2 border-l-4 border-amber-400">
                <div className="flex items-center gap-2 text-amber-700 mb-2">
                  <FileText className="w-4 h-4" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Notas del Conductor
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {delivery.notes_driver}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg font-montserrat"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeliverySearchModal;